/**
 * Dynamic Handle Management System
 *
 * Manages input/output handles based on AI model parameter configuration.
 * Handles dynamic creation, matching, and removal of node handles when model changes.
 *
 * Key Concepts:
 * - Default handles: t1 (text), i1 (image) - pre-existing on node
 * - Dynamic handles: Created from inputParams/outputParams
 * - Matching: Type + order based (first text → t1, second text → t2)
 * - Cleanup: Remove handles that no longer exist in new model
 */

import type { HandleMeta, HandleDataType } from '../types'
import type { MergedParameter } from '@/lib/ai/parameter-merge'
import { getHandleIcon } from '../configs/handle-icons'

// ============================================================================
// TYPES
// ============================================================================

/**
 * Handle type abbreviations for ID generation
 */
const HANDLE_TYPE_PREFIXES: Record<string, string> = {
  text: 't',
  image: 'i',
  video: 'v',
  audio: 'a',
  mask: 'm',
  file: 'f',
  '3d': '3',
  code: 'c',
  number: 'n',
  boolean: 'b',
  any: 'x',
}

/**
 * Get handle ID prefix for a data type
 */
function getHandlePrefix(dataType: string): string {
  return HANDLE_TYPE_PREFIXES[dataType] || 'h'
}

// ============================================================================
// HANDLE ID GENERATION
// ============================================================================

/**
 * Generate handle ID from data type and order
 * @example
 * generateHandleId('text', 0) → 't1'
 * generateHandleId('text', 1) → 't2'
 * generateHandleId('image', 0) → 'i1'
 */
export function generateHandleId(dataType: string, order: number): string {
  const prefix = getHandlePrefix(dataType)
  return `${prefix}${order + 1}` // 1-based indexing (t1, t2, not t0, t1)
}

// ============================================================================
// HANDLE MATCHING
// ============================================================================

/**
 * Match input parameters to existing handles
 *
 * Logic:
 * - Group parameters by type (text, image, etc.)
 * - Match to existing handles of same type in order
 * - Return mapping: parameterName → handleId
 *
 * @example
 * Existing: [{ id: 't1', dataType: 'text' }, { id: 'i1', dataType: 'image' }]
 * Params: [{ name: 'prompt', inputType: 'text' }, { name: 'negative_prompt', inputType: 'text' }]
 * Result: { 'prompt': 't1', 'negative_prompt': null } // t2 will be created
 */
export function matchParametersToHandles(
  inputParams: MergedParameter[],
  existingHandles: HandleMeta[]
): Map<string, string | null> {
  const mapping = new Map<string, string | null>()

  // Group existing handles by type
  const handlesByType = new Map<string, string[]>()
  existingHandles.forEach((handle) => {
    if (handle.type === 'input') {
      const dataType = handle.dataType || 'any'
      if (!handlesByType.has(dataType)) {
        handlesByType.set(dataType, [])
      }
      handlesByType.get(dataType)!.push(handle.id)
    }
  })

  // Group parameters by type and order
  const paramsByType = new Map<string, MergedParameter[]>()
  inputParams.forEach((param) => {
    const type = param.inputType || 'text'
    if (!paramsByType.has(type)) {
      paramsByType.set(type, [])
    }
    paramsByType.get(type)!.push(param)
  })

  // Match parameters to handles
  paramsByType.forEach((params, type) => {
    const availableHandles = handlesByType.get(type) || []

    params.forEach((param, index) => {
      // Match to existing handle if available
      if (index < availableHandles.length) {
        mapping.set(param.name, availableHandles[index])
      } else {
        // Mark for creation
        mapping.set(param.name, null)
      }
    })
  })

  return mapping
}

// ============================================================================
// HANDLE CREATION
// ============================================================================

/**
 * Create new input handles from parameters
 *
 * @param inputParams - Parameters marked as input
 * @param existingHandles - Current handles on node
 * @param parameterMapping - Result from matchParametersToHandles
 * @returns Array of new handles to add
 */
export function createInputHandles(
  inputParams: MergedParameter[],
  existingHandles: HandleMeta[],
  parameterMapping: Map<string, string | null>
): HandleMeta[] {
  const newHandles: HandleMeta[] = []

  // Count existing handles by type for ID generation
  // globalInputCount ensures ordered values are globally unique per side,
  // preventing two handles of different types from overlapping visually
  const handleCountsByType = new Map<string, number>()
  let globalInputCount = 0
  existingHandles.forEach((handle) => {
    if (handle.type === 'input') {
      const dataType = handle.dataType || 'any'
      handleCountsByType.set(dataType, (handleCountsByType.get(dataType) || 0) + 1)
      globalInputCount++
    }
  })

  inputParams.forEach((param) => {
    const existingHandleId = parameterMapping.get(param.name)

    // Only create if not matched to existing handle
    if (existingHandleId === null) {
      const dataType = param.inputType || 'text'
      const currentCount = handleCountsByType.get(dataType) || 0

      // Array-type parameters accept multiple connections (e.g., image_urls: string[])
      const isArrayParam = param.type === 'array'

      const newHandle: HandleMeta = {
        id: generateHandleId(dataType, currentCount),
        type: 'input',
        position: { side: 'left', ordered: globalInputCount },
        dataType: dataType as HandleDataType,
        label: param.label, // ✅ Handle label for display
        description: param.description, // ✅ Handle description for tooltip
        ...(isArrayParam ? {} : { maxConnections: 1 }),
        // Store parameter name in handle for reverse lookup
        metadata: {
          parameterName: param.name,
          parameterLabel: param.label,
          icon: getHandleIcon(param.name, param.inputType),
        },
      }

      newHandles.push(newHandle)
      handleCountsByType.set(dataType, currentCount + 1)
      globalInputCount++
    }
  })

  return newHandles
}

// ============================================================================
// HANDLE REMOVAL
// ============================================================================

/**
 * Find handles that should be removed
 *
 * Logic:
 * - Handles not matched to any parameter should be removed
 * - Keep locked handles (like generated-output)
 *
 * @returns Array of handle IDs to remove
 */
export function findHandlesToRemove(
  existingHandles: HandleMeta[],
  parameterMapping: Map<string, string | null>
): string[] {
  const matchedHandleIds = new Set(
    Array.from(parameterMapping.values()).filter((id) => id !== null)
  )

  const toRemove: string[] = []

  existingHandles.forEach((handle) => {
    // Skip output handles and locked handles
    if (handle.type === 'output' || handle.locked) {
      return
    }

    // Remove if not matched to any parameter
    if (!matchedHandleIds.has(handle.id)) {
      toRemove.push(handle.id)
    }
  })

  return toRemove
}

// ============================================================================
// HANDLE UPDATE (Main Function)
// ============================================================================

/**
 * Update node handles based on new input parameters
 *
 * This is the main function that orchestrates:
 * 1. Matching existing handles to new parameters
 * 2. Creating new handles for unmatched parameters
 * 3. Updating metadata for reused handles (label, description, icon)
 * 4. Removing handles no longer needed
 *
 * @param inputParams - Input parameters from merged model schema
 * @param existingHandles - Current handles on the node
 * @returns Update instructions: { add, update, remove, mapping }
 */
export function updateNodeHandles(
  inputParams: MergedParameter[],
  existingHandles: HandleMeta[]
): {
  handlesToAdd: HandleMeta[]
  handlesToUpdate: Array<{ id: string; updates: Partial<HandleMeta> }>
  handlesToRemove: string[]
  parameterMapping: Map<string, string>
} {
  // Step 1: Match parameters to existing handles
  const mapping = matchParametersToHandles(inputParams, existingHandles)

  // Step 2: Create new handles for unmatched parameters
  const handlesToAdd = createInputHandles(inputParams, existingHandles, mapping)

  // Step 3: Update metadata for reused handles (avoid duplicates)
  const handlesToUpdate: Array<{ id: string; updates: Partial<HandleMeta> }> = []
  const updatedHandleIds = new Set<string>()

  inputParams.forEach((param) => {
    const existingHandleId = mapping.get(param.name)

    if (existingHandleId && !updatedHandleIds.has(existingHandleId)) {
      // Handle was reused → Update its metadata (only once per handle)
      handlesToUpdate.push({
        id: existingHandleId,
        updates: {
          label: param.label,
          description: param.description,
          metadata: {
            parameterName: param.name,
            parameterLabel: param.label,
            icon: getHandleIcon(param.name, param.inputType),
          },
        },
      })
      updatedHandleIds.add(existingHandleId)
    }
  })

  // Step 4: Find handles to remove
  const handlesToRemove = findHandlesToRemove(existingHandles, mapping)

  // Step 5: Build final parameter → handle ID mapping
  const parameterMapping = new Map<string, string>()
  mapping.forEach((handleId, paramName) => {
    if (handleId !== null) {
      parameterMapping.set(paramName, handleId)
    }
  })

  // Add newly created handles to mapping
  handlesToAdd.forEach((handle) => {
    const paramName = handle.metadata?.parameterName
    if (paramName) {
      parameterMapping.set(paramName, handle.id)
    }
  })

  return {
    handlesToAdd,
    handlesToUpdate,
    handlesToRemove,
    parameterMapping,
  }
}

// ============================================================================
// OUTPUT HANDLES
// ============================================================================

/**
 * Create output handles from output parameters
 *
 * @param outputParams - Parameters marked as output with outputMode='handle'
 * @returns Array of output handles
 */
export function createOutputHandles(outputParams: MergedParameter[]): HandleMeta[] {
  const outputHandles: HandleMeta[] = []

  // Count outputs by type for ID generation, global counter for visual ordering
  const outputCountsByType = new Map<string, number>()
  let globalOutputCount = 0

  outputParams
    .filter((param) => param.outputMode === 'handle')
    .forEach((param) => {
      const dataType = param.outputType || 'any'
      const currentCount = outputCountsByType.get(dataType) || 0

      const handle: HandleMeta = {
        id: `${param.name}-output`,
        type: 'output',
        position: { side: 'right', ordered: globalOutputCount },
        dataType: dataType as HandleDataType,
        label: param.label, // ✅ Handle label for display
        description: param.description, // ✅ Handle description for tooltip
        metadata: {
          parameterName: param.name,
          parameterLabel: param.label,
          icon: getHandleIcon(param.name, param.outputType),
        },
      }

      outputHandles.push(handle)
      outputCountsByType.set(dataType, currentCount + 1)
      globalOutputCount++
    })

  return outputHandles
}
